// Mobile parity: streetAddress + city + zipCode + country + state.
// Stored in profile.address as {streetAddress, city, zipCode, country, state}.
// Country/state list bundled inline to avoid a new dependency.
import { useEffect, useState } from "react"
import { useOnboarding } from "../../contexts/OnboardingContext"
import {
  OnboardingStepShell,
  ContinueButton,
  CardField,
  InlineDropdown,
  OB_COLORS as C,
  BARE_INPUT_STYLE,
} from "./OnboardingStepShell"

// Minimal country → states list for the most common ones. Users of other
// countries fall back to a free-text state field (same as mobile when the
// country library returns no states).
const COUNTRY_STATES = {
  "India": [
    "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa",
    "Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
    "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan",
    "Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
    "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu",
    "Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry",
  ],
  "United States": [
    "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
    "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky",
    "Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi",
    "Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico",
    "New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
    "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
    "Virginia","Washington","West Virginia","Wisconsin","Wyoming","District of Columbia",
  ],
  "United Kingdom": ["England","Scotland","Wales","Northern Ireland"],
  "Canada": [
    "Alberta","British Columbia","Manitoba","New Brunswick","Newfoundland and Labrador",
    "Nova Scotia","Ontario","Prince Edward Island","Quebec","Saskatchewan",
    "Northwest Territories","Nunavut","Yukon",
  ],
  "Australia": [
    "Australian Capital Territory","New South Wales","Northern Territory","Queensland",
    "South Australia","Tasmania","Victoria","Western Australia",
  ],
  "Singapore":   [],
  "UAE":         ["Abu Dhabi","Dubai","Sharjah","Ajman","Umm Al Quwain","Ras Al Khaimah","Fujairah"],
  "Germany":     ["Baden-Württemberg","Bavaria","Berlin","Brandenburg","Bremen","Hamburg","Hesse","Lower Saxony","Mecklenburg-Vorpommern","North Rhine-Westphalia","Rhineland-Palatinate","Saarland","Saxony","Saxony-Anhalt","Schleswig-Holstein","Thuringia"],
  "France":      ["Auvergne-Rhône-Alpes","Bourgogne-Franche-Comté","Brittany","Centre-Val de Loire","Corsica","Grand Est","Hauts-de-France","Île-de-France","Normandy","Nouvelle-Aquitaine","Occitanie","Pays de la Loire","Provence-Alpes-Côte d'Azur"],
  "Netherlands": ["Drenthe","Flevoland","Friesland","Gelderland","Groningen","Limburg","North Brabant","North Holland","Overijssel","South Holland","Utrecht","Zeeland"],
  "Ireland":     ["Carlow","Cavan","Clare","Cork","Donegal","Dublin","Galway","Kerry","Kildare","Kilkenny","Laois","Leitrim","Limerick","Longford","Louth","Mayo","Meath","Monaghan","Offaly","Roscommon","Sligo","Tipperary","Waterford","Westmeath","Wexford","Wicklow"],
  "Japan":       ["Hokkaido","Aomori","Iwate","Miyagi","Akita","Yamagata","Fukushima","Ibaraki","Tochigi","Gunma","Saitama","Chiba","Tokyo","Kanagawa","Niigata","Toyama","Ishikawa","Fukui","Yamanashi","Nagano","Gifu","Shizuoka","Aichi","Mie","Shiga","Kyoto","Osaka","Hyogo","Nara","Wakayama","Tottori","Shimane","Okayama","Hiroshima","Yamaguchi","Tokushima","Kagawa","Ehime","Kochi","Fukuoka","Saga","Nagasaki","Kumamoto","Oita","Miyazaki","Kagoshima","Okinawa"],
  "South Korea": ["Seoul","Busan","Daegu","Incheon","Gwangju","Daejeon","Ulsan","Sejong","Gyeonggi","Gangwon","North Chungcheong","South Chungcheong","North Jeolla","South Jeolla","North Gyeongsang","South Gyeongsang","Jeju"],
  "Pakistan":    ["Punjab","Sindh","Khyber Pakhtunkhwa","Balochistan","Islamabad Capital Territory","Gilgit-Baltistan","Azad Kashmir"],
  "Malaysia":    ["Johor","Kedah","Kelantan","Melaka","Negeri Sembilan","Pahang","Perak","Perlis","Pulau Pinang","Sabah","Sarawak","Selangor","Terengganu","Kuala Lumpur","Labuan","Putrajaya"],
  "Philippines": ["Metro Manila","Cordillera Administrative Region","Ilocos Region","Cagayan Valley","Central Luzon","Calabarzon","Mimaropa","Bicol Region","Western Visayas","Central Visayas","Eastern Visayas","Zamboanga Peninsula","Northern Mindanao","Davao Region","Soccsksargen","Caraga","Bangsamoro Autonomous Region"],
  "Brazil":      ["Acre","Alagoas","Amapá","Amazonas","Bahia","Ceará","Distrito Federal","Espírito Santo","Goiás","Maranhão","Mato Grosso","Mato Grosso do Sul","Minas Gerais","Pará","Paraíba","Paraná","Pernambuco","Piauí","Rio de Janeiro","Rio Grande do Norte","Rio Grande do Sul","Rondônia","Roraima","Santa Catarina","São Paulo","Sergipe","Tocantins"],
  "South Africa":["Eastern Cape","Free State","Gauteng","KwaZulu-Natal","Limpopo","Mpumalanga","Northern Cape","North West","Western Cape"],
  "New Zealand": ["Auckland","Bay of Plenty","Canterbury","Gisborne","Hawke's Bay","Manawatū-Whanganui","Marlborough","Nelson","Northland","Otago","Southland","Taranaki","Tasman","Waikato","Wellington","West Coast"],
  "Sweden":      ["Blekinge","Dalarna","Gävleborg","Gotland","Halland","Jämtland","Jönköping","Kalmar","Kronoberg","Norrbotten","Örebro","Östergötland","Skåne","Södermanland","Stockholm","Uppsala","Värmland","Västerbotten","Västernorrland","Västmanland","Västra Götaland"],
}
const COUNTRY_LIST = Object.keys(COUNTRY_STATES).sort()

export default function LocationStep({ onContinue, onBack, step, totalSteps }) {
  const { profile, updateField } = useOnboarding()
  const addr0 = profile?.address || {}

  const [address, setAddress] = useState(addr0.streetAddress || "")
  const [city,    setCity]    = useState(addr0.city?.trim() ? addr0.city : "Bangalore")
  const [zip,     setZip]     = useState(addr0.zipCode || "")
  const [country, setCountry] = useState(addr0.country?.trim() ? addr0.country : "India")
  const [state,   setState]   = useState(addr0.state?.trim() ? addr0.state : "Karnataka")
  const [openId,  setOpenId]  = useState(null)

  const stateList = COUNTRY_STATES[country] || []
  const hasPresetStates = stateList.length > 0

  // Reset state when country changes if it isn't in the new list.
  useEffect(() => {
    if (!hasPresetStates) return
    if (!stateList.includes(state)) setState("")
  }, [country])

  const allFilled =
    address.trim() !== "" &&
    city.trim() !== "" &&
    zip.trim() !== "" &&
    country.trim() !== "" &&
    state.trim() !== ""

  const continueOn = () => {
    if (!allFilled) return
    updateField({
      address: {
        streetAddress: address.trim(),
        city:          city.trim(),
        zipCode:       zip.trim(),
        country,
        state:         state.trim(),
      },
    })
    onContinue()
  }

  return (
    <OnboardingStepShell
      title={"Where are\nyou based?"}
      subtitle={"Employers ask for this on almost every application."}
      step={step}
      totalSteps={totalSteps}
      onBack={onBack}
      bottom={<ContinueButton label="Continue" onClick={continueOn} disabled={!allFilled} />}
    >
      <div onClick={() => setOpenId(null)} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {/* Area / Locality */}
        <div onClick={(e) => e.stopPropagation()}>
          <div style={{ fontSize: 13, fontWeight: 500, color: C.text, marginBottom: 6 }}>Area / Locality</div>
          <CardField
            prefix={
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M10 2a6 6 0 00-6 6c0 4.5 6 10 6 10s6-5.5 6-10a6 6 0 00-6-6z" stroke={C.textMuted} strokeWidth="1.4" />
                <circle cx="10" cy="8" r="2" stroke={C.textMuted} strokeWidth="1.4" />
              </svg>
            }
          >
            <input
              type="text"
              placeholder="Whitefield"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              style={BARE_INPUT_STYLE}
            />
          </CardField>
        </div>

        {/* City (75%) + ZIP (25%) */}
        <div style={{ display: "grid", gridTemplateColumns: "7fr 3fr", gap: 10 }} onClick={(e) => e.stopPropagation()}>
          <CardField label="City">
            <input
              type="text"
              placeholder="Bangalore"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              style={BARE_INPUT_STYLE}
            />
          </CardField>
          <CardField label="ZIP">
            <input
              type="text"
              placeholder="560001"
              inputMode="numeric"
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/[^0-9A-Za-z\s-]/g, ""))}
              style={BARE_INPUT_STYLE}
            />
          </CardField>
        </div>

        {/* Country */}
        <div onClick={(e) => e.stopPropagation()}>
          <InlineDropdown
            id="country"
            label="Country"
            value={country || null}
            hint="Select country..."
            options={COUNTRY_LIST}
            openId={openId}
            setOpenId={setOpenId}
            onSelect={(v) => { setCountry(v); setState("") }}
          />
        </div>

        {/* State */}
        <div onClick={(e) => e.stopPropagation()}>
          {hasPresetStates ? (
            <InlineDropdown
              id="state"
              label="State / Province"
              value={stateList.includes(state) ? state : null}
              hint="Select state..."
              options={stateList}
              openId={openId}
              setOpenId={setOpenId}
              onSelect={(v) => setState(v)}
            />
          ) : (
            <CardField label="State / Province" suffix={
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <circle cx="9" cy="9" r="5" stroke={C.textMuted} strokeWidth="1.4" />
                <path d="M13 13l4 4" stroke={C.textMuted} strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            }>
              <input
                type="text"
                placeholder="Enter state / province"
                value={state}
                onChange={(e) => setState(e.target.value)}
                style={BARE_INPUT_STYLE}
              />
            </CardField>
          )}
        </div>
      </div>
    </OnboardingStepShell>
  )
}
